import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './ContactPage.scss';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

    useEffect(() => {
        const body = document.body;
        const hadNoZoom = body.classList.contains('no-zoom');
        body.classList.remove('no-zoom');

        return () => {
            if (hadNoZoom) {
                body.classList.add('no-zoom');
            }
        };
    }, []);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear status message when user starts typing
        if (submitStatus) {
            setSubmitStatus(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        // Prepare form data for FormSubmit
        const formDataToSend = new FormData();
        formDataToSend.append('name', formData.name);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('phone', formData.phone || 'Not provided');
        formDataToSend.append('subject', formData.subject);
        formDataToSend.append('message', formData.message);
        formDataToSend.append('_subject', 'New Contact Form Submission from CHAIropractic');
        formDataToSend.append('_captcha', 'false');

        try {
            const response = await fetch('https://formsubmit.co/ajax/issac@chairopractic.com', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json'
                },
                body: formDataToSend
            });

            const data = await response.json();

            if (response.ok && data.success) {
                setSubmitStatus('success');
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    subject: '',
                    message: ''
                });
                // Clear success message after 5 seconds
                setTimeout(() => {
                    setSubmitStatus(null);
                }, 5000);
            } else {
                throw new Error(data.message || 'Form submission failed');
            }
        } catch (error) {
            console.error('FormSubmit Error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page">
            <div className="contact-hero">
                <h1>Contact Us</h1>
                <p>We're here to help you on your journey to better health</p>
            </div>

            <div className="contact-container">
                <div className="contact-grid">
                    <div className="contact-info">
                        <h2>Get In Touch</h2>
                        <p>
                            Ready to take the first step toward better health? We're here to answer your questions 
                            and help you schedule your appointment. Reach out to us using any of the methods below.
                        </p>

                        <div className="contact-methods">
                            <div className="contact-method">
                                <div className="method-icon">📞</div>
                                <div className="method-content">
                                    <h3>Phone</h3>
                                    <p className="contact-info">(949) 371-9377</p>
                                </div>
                            </div>

                            <div className="contact-method">
                                <div className="method-icon">🕐</div>
                                <div className="method-content hours-content">
                                    <h3>Hours</h3>
                                    <p>Monday: <b>10am - 6pm</b></p>
                                    <p>Tuesday: <b>Appointments Only</b></p>
                                    <p>Wednesday: <b>10am - 6pm</b></p>
                                    <p>Thursday: <b>Appointments Only</b></p>
                                    <p>Friday: <b>10am - 6pm</b></p>
                                </div>
                            </div>

                            <div className="contact-method">
                                <div className="method-icon">📍</div>
                                <div className="method-content">
                                    <h3>Location</h3>
                                    <p className="address">14232 Red Hill Ave STE B, Tustin, CA 92780</p>
                                </div>
                            </div>
                        </div>

                        <div className="map-container">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3315.271498729902!2d-117.81341982348092!3d33.76382997322448!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80dd27c8d8f5c577%3A0xb3b4f9e7f2e7e3e3!2s14232%20Red%20Hill%20Ave%20STE%20B%2C%20Tustin%2C%20CA%2092780!5e0!3m2!1sen!2sus!4v1703856000000!5m2!1sen!2sus"
                                width="100%"
                                height="400"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="CHAIropractic Location"
                            ></iframe>
                        </div>
                    </div>

                    <div className="contact-form-section">
                        <h2>Send Us a Message</h2>
                        <p>Fill out the form below and we'll get back to you as soon as possible.</p>
                        
                        <div className="contact-method email-recipient">
                            <div className="method-icon">✉️</div>
                            <div className="method-content">
                                <h3>Your message will be sent to:</h3>
                                <p className="contact-info">issac@chairopractic.com</p>
                            </div>
                        </div>
                        
                        {submitStatus === 'success' && (
                            <div className="form-message form-message-success">
                                ✓ Message sent successfully! We'll get back to you soon.
                            </div>
                        )}
                        {submitStatus === 'error' && (
                            <div className="form-message form-message-error">
                                ✗ There was an error sending your message. Please try again or contact us directly at issac@chairopractic.com
                            </div>
                        )}
                        
                        <form 
                            className="contact-form" 
                            onSubmit={handleSubmit}
                        >
                            <div className="form-group">
                                <label htmlFor="name">Name *</label>
                                <input 
                                    type="text" 
                                    id="name" 
                                    name="name" 
                                    placeholder="Enter your name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address *</label>
                                <input 
                                    type="email" 
                                    id="email" 
                                    name="email" 
                                    placeholder="Enter your email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required 
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="phone">Phone Number</label>
                                <input 
                                    type="tel" 
                                    id="phone" 
                                    name="phone" 
                                    placeholder="Enter your phone number (optional)"
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="subject">Subject *</label>
                                <select 
                                    id="subject" 
                                    name="subject" 
                                    value={formData.subject}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Select a subject</option>
                                    <option value="appointment">Book Appointment</option>
                                    <option value="question">General Question</option>
                                    <option value="consultation">Free Consultation</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="message">Message *</label>
                                <textarea 
                                    id="message" 
                                    name="message" 
                                    rows="5"
                                    placeholder="Tell us how we can help you"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                ></textarea>
                            </div>

                            <button 
                                type="submit" 
                                className="btn-submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>
                </div>

            </div>

            <div className="cta-banner">
                <h2>Ready to Start Your Journey?</h2>
                <p>Schedule your consultation today and take the first step toward optimal health</p>
                <Link to="/contact" className="btn-primary-large">Call (949) 371-9377</Link>
            </div>
        </div>
    );
};

export default ContactPage;

